import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge, budgetColor } from "@/mockups/ui";
import { toast } from "sonner";
import {
  Building2, FolderKanban, Clock, User, ArrowRight, Sparkles, Plus,
} from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { fmtHM } from "@/lib/utils";

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function Dashboard() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const { data, loading, refetch } = useFetch("/dashboard/stats");
  const [seeding, setSeeding] = useState(false);

  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/seed/demo");
      toast.success("Datos de ejemplo cargados");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    } finally {
      setSeeding(false);
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const empty = data.companies === 0 && data.total_projects === 0;
  const perDay = (data.per_day || []).map((d, i) => ({ day: DAY_LABELS[i], hours: d.hours }));

  const KPIS = [
    { label: "Clientes / empresas", value: data.companies, icon: Building2, accent: "text-primary bg-primary/12" },
    { label: "Proyectos activos", value: data.active_projects, icon: FolderKanban, accent: "text-chart-4 bg-chart-4/12" },
    { label: "Horas equipo (semana)", value: fmtHM(data.hours_week), icon: Clock, accent: "text-gold bg-gold/15" },
    { label: "Mis horas (semana)", value: fmtHM(data.my_hours_week), icon: User, accent: "text-primary bg-primary/12" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title={`Hola, ${(user?.name || "").split(" ")[0]} 👋`} subtitle="Resumen de la actividad de la agencia">
        {can("projects", "create") && (
          <Button size="sm" className="gap-1.5" onClick={() => navigate("/proyectos")} data-testid="dash-new-project"><Plus className="h-4 w-4" /> Nuevo proyecto</Button>
        )}
      </PageHeader>

      {empty && (
        <Card className="border-dashed border-2 shadow-none p-10 text-center">
          <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-6 w-6" />
          </div>
          <h3 className="font-heading font-bold text-lg">Aún no hay datos</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Empieza creando tus clientes y proyectos, o carga un set de datos de ejemplo para explorar la herramienta.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Button variant="outline" onClick={() => navigate("/crm/empresas")} data-testid="empty-go-crm">Ir a Empresas</Button>
            {user?.role === "Administrador" && (
              <Button onClick={seed} disabled={seeding} className="gap-1.5" data-testid="seed-demo-btn">
                <Sparkles className="h-4 w-4" /> {seeding ? "Cargando…" : "Cargar datos de ejemplo"}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k, i) => (
          <Card key={i} className="p-5 border-border shadow-none hover:shadow-sm transition-shadow duration-200" data-testid={`kpi-${i}`}>
            <div className={`h-10 w-10 rounded-md flex items-center justify-center ${k.accent}`}>
              <k.icon className="h-5 w-5" />
            </div>
            <div className="mt-4 font-heading font-bold text-3xl tracking-tight tabular-nums">{k.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{k.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Week hours */}
          <Card className="p-5 border-border shadow-none">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold text-base">Horas registradas · esta semana</h3>
              <span className="text-xs text-muted-foreground">Total <span className="font-semibold text-foreground tabular-nums">{fmtHM(data.hours_week)}</span></span>
            </div>
            <div className="h-52 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perDay} margin={{ top: 8, right: 0, left: -24, bottom: 0 }}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", fontSize: 12, background: "hsl(var(--card))" }} formatter={(v) => [fmtHM(v), "Horas"]} />
                  <Bar dataKey="hours" radius={[4, 4, 0, 0]} maxBarSize={44}>
                    {perDay.map((_, i) => <Cell key={i} fill={i < 5 ? "hsl(var(--primary))" : "hsl(var(--muted))"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Top projects */}
          <Card className="border-border shadow-none overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-3">
              <h3 className="font-heading font-bold text-base">Proyectos · rentabilidad</h3>
              <Link to="/proyectos" className="text-xs text-primary font-medium flex items-center gap-1 hover:gap-1.5 transition-all duration-200">Ver todos <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            {(data.top_projects || []).length === 0 ? (
              <p className="px-5 pb-5 text-sm text-muted-foreground">Sin proyectos todavía.</p>
            ) : (
              <div className="divide-y divide-border">
                {data.top_projects.map((p) => {
                  const pct = p.budget_hours > 0 ? Math.round((p.hours_spent / p.budget_hours) * 100) : 0;
                  return (
                    <Link key={p.id} to={`/proyecto/${p.id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors duration-200" data-testid={`dash-project-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{p.name}</div>
                      </div>
                      <div className="w-40 hidden sm:block">
                        <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                          <span className="tabular-nums">{fmtHM(p.hours_spent)} / {p.budget_hours ? fmtHM(p.budget_hours) : "–"}</span>
                          {p.budget_hours > 0 && <span className={pct >= 100 ? "text-destructive font-semibold" : "tabular-nums"}>{pct}%</span>}
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full rounded-full ${budgetColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* My tasks */}
        <Card className="border-border shadow-none h-fit">
          <div className="p-5 pb-3 flex items-center justify-between">
            <h3 className="font-heading font-bold text-base">Mis tareas</h3>
            <span className="text-xs text-muted-foreground tabular-nums">{(data.my_tasks || []).length} abiertas</span>
          </div>
          {(data.my_tasks || []).length === 0 ? (
            <p className="px-5 pb-5 text-sm text-muted-foreground">No tienes tareas asignadas.</p>
          ) : (
            <div className="divide-y divide-border">
              {data.my_tasks.map((t) => (
                <div key={t.id} className="px-5 py-3 hover:bg-muted/40 transition-colors duration-200" data-testid={`dash-task-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium leading-snug">{t.title}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t.project_name}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
